// Gateway client for KAI Sidekick. Mirrors the AIBuilderTab `apiCall` pattern:
// hits ${REACT_APP_AI_BUILDER_URL}/api/sidekick/* with merchantId + sessionId
// headers read from localStorage.
const BASE = process.env.REACT_APP_AI_BUILDER_URL || "http://localhost:4000";

function headers() {
  return {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache",
    Pragma: "no-cache",
    merchantId: localStorage.getItem("merchantId") || "",
    sessionId: localStorage.getItem("sessionId") || "",
  };
}

async function call(path, options = {}) {
  let res;
  try {
    res = await fetch(`${BASE}/api${path}`, {
      cache: "no-store",
      ...options,
      headers: { ...headers(), ...(options.headers || {}) },
    });
  } catch (err) {
    const networkErr = new Error(
      `Cannot reach KAI at ${BASE}. Is the gateway running on port 4000?`
    );
    networkErr.cause = err;
    throw networkErr;
  }
  // 409 (no matching pending action) is a meaningful response, not a transport error.
  if (!res.ok && res.status !== 409) {
    let body = {};
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    const err = new Error(body.error || `KAI API error: ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return res.json();
}

export function kaiErrorDescription(err, fallback = "Is the AI service running?") {
  if (!err) return fallback;
  const detail = err.body?.details || err.body?.error || err.message;
  if (!detail || detail === "Internal server error") {
    return `${fallback} Restart the gateway (npm run dev in kuber-ai-service) if you recently pulled changes.`;
  }
  return detail;
}

export const kaiClient = {
  start: () => call("/sidekick/start", { method: "POST", body: JSON.stringify({}) }),
  sendMessage: (sessionId, message, contextProductId, extra = {}) =>
    call(`/sidekick/${sessionId}/message`, {
      method: "POST",
      body: JSON.stringify({ message, contextProductId, ...extra }),
    }),
  confirm: (sessionId, actionId) =>
    call(`/sidekick/${sessionId}/confirm`, {
      method: "POST",
      body: JSON.stringify({ actionId }),
    }),
  cancel: (sessionId, actionId) =>
    call(`/sidekick/${sessionId}/cancel`, {
      method: "POST",
      body: JSON.stringify({ actionId }),
    }),
  history: (sessionId) => call(`/sidekick/${sessionId}/history`),

  // Server-side speech-to-text (Whisper) - reliable where the browser's Google
  // recognizer is blocked.
  transcribe: (audioBase64, mimeType) =>
    call("/voice/transcribe", { method: "POST", body: JSON.stringify({ audioBase64, mimeType }) }),
  // Server-side text-to-speech (OpenAI) - natural voice that works even when the
  // browser has no English voice installed.
  speak: (text, voice) =>
    call("/voice/speak", { method: "POST", body: JSON.stringify({ text, voice }) }),
};
